import { useState } from 'react';
import { Code, Copy, Check, AlertCircle, Sparkles } from 'lucide-react';
import { Button, Label, Textarea, Alert, AlertDescription, Skeleton } from '@aaas/shared';
import type { EmbedCodeResponse } from '../../types/widget.types';

interface WidgetEmbedCodePanelProps {
  widgetName?: string;
  embedCode?: EmbedCodeResponse;
  isLoading?: boolean;
  error?: Error | null;
}

export function WidgetEmbedCodePanel({
  widgetName,
  embedCode,
  isLoading,
  error,
}: WidgetEmbedCodePanelProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="h-[700px] bg-white rounded-2xl shadow-xl border-2 border-gray-200 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 bg-gradient-to-br from-purple-50 to-indigo-50 border-b border-purple-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white border-2 border-purple-200 flex items-center justify-center shadow-sm">
            <Code className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">Embed Code</h2>
            {widgetName && <p className="text-sm text-gray-600">for {widgetName}</p>}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {!widgetName ? (
          // Empty state - no widget selected
          <div className="flex flex-col items-center justify-center h-full text-center space-y-4 text-gray-500">
            <Sparkles className="w-16 h-16 text-gray-300" />
            <div>
              <p className="text-lg font-medium text-gray-700">Select a Widget</p>
              <p className="text-sm text-gray-500 mt-1">Choose a widget to view its embed code</p>
            </div>
          </div>
        ) : isLoading ? (
          // Loading state
          <div className="space-y-6">
            <Skeleton variant="rectangular" className="w-full h-40" />
            <Skeleton variant="rectangular" className="w-full h-32" />
            <Skeleton variant="rectangular" className="w-full h-32" />
          </div>
        ) : error ? (
          // Error state
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Failed to generate embed code</AlertDescription>
          </Alert>
        ) : embedCode ? (
          // Embed code display
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold text-gray-900">Embed Code</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(embedCode.embed_code, 'full')}
                className={copiedField === 'full' ? 'border-green-500 text-green-600' : ''}
              >
                {copiedField === 'full' ? (
                  <>
                    <Check className="w-3 h-3 mr-1" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3 mr-1" />
                    Copy
                  </>
                )}
              </Button>
            </div>
            <Textarea
              value={embedCode.embed_code}
              readOnly
              rows={20}
              className="font-mono text-xs bg-gray-50 border-gray-300"
            />
            <p className="text-xs text-gray-500">
              Copy and paste this code into your website's HTML, just before the closing{' '}
              <code>&lt;/body&gt;</code> tag.
            </p>
          </div>
        ) : null}
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 px-6 py-3 bg-gray-50 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          Copy the code and paste it into your website's HTML
        </p>
      </div>
    </div>
  );
}
